import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';
import {Subject, fromEvent} from 'rxjs';
import {map, switchMap, takeUntil} from 'rxjs/operators';
import {PopupManagerService} from '../../../core/services/popup-manager.service';

interface IVector2 {
  x: number;
  y: number;
}

@Directive({
  selector: '[appDraggableDialog]',
  standalone: true
})
export class DraggableDialogDirective implements AfterViewInit, OnDestroy, OnInit {
  @Input() draggableKey: string;
  @Input() dialogClass = '.q9-map-tree';
  @Input() draggableWithRestrictArea = false;
  @Input() draggableAreaTopMargin = 74;
  @Input() draggableAreaBotMargin = 10;
  @Input() draggableAreaXMargin = 20;
  private target: HTMLElement;
  private handle: HTMLElement;
  private delta = {x: 0, y: 0};
  private startOffset = [0, 0];
  private pageSize: IVector2;
  private clientSize: IVector2;
  private availableCoords: {topLeft: IVector2; bottomRight: IVector2};

  private destroy$ = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private zone: NgZone,
    private popupSvc: PopupManagerService
  ) {}

  ngOnInit() {
    if (this.popupSvc.positions[this.draggableKey]) {
      this.startOffset = [
        this.popupSvc.positions[this.draggableKey][0],
        this.popupSvc.positions[this.draggableKey][1]
      ];
    }
  }

  public ngAfterViewInit(): void {
    this.handle = this.elementRef.nativeElement;
    const classSelector = this.dialogClass.startsWith('.')
      ? this.dialogClass
      : `.${this.dialogClass}`;

    this.target = document.querySelector(classSelector) as HTMLElement;
    this.handle.style.cursor = 'move';

    if (!this.popupSvc.positions[this.draggableKey]) {
      this.startOffset = [this.target.offsetLeft, this.target.offsetTop];
    }

    this.setupEvents();

    if (this.draggableWithRestrictArea) {
      this.calculateAvailableArea();
    }

    this.translate();
  }

  public ngOnDestroy(): void {
    this.popupSvc.positions[this.draggableKey] = [this.target.offsetLeft, this.target.offsetTop];
    this.destroy$.next();
  }

  private setupEvents() {
    this.zone.runOutsideAngular(() => {
      const mousedown$ = fromEvent(this.handle, 'mousedown');
      const mousemove$ = fromEvent(document, 'mousemove');
      const mouseup$ = fromEvent(document, 'mouseup');
      const mousedrag$ = mousedown$.pipe(
        takeUntil(this.destroy$),
        switchMap((event: MouseEvent) => {
          const startX = event.clientX;
          const startY = event.clientY;

          if (this.draggableWithRestrictArea) {
            this.calculateAvailableArea();
          }

          this.startOffset = [this.target.offsetLeft, this.target.offsetTop];

          return mousemove$.pipe(
            takeUntil(mouseup$),
            map((e: MouseEvent) => {
              e.preventDefault();
              this.delta = {
                x: e.clientX - startX,
                y: e.clientY - startY
              };
            })
          );
        })
      );

      mousedrag$.subscribe(() => {
        if (this.delta.x === 0 && this.delta.y === 0) {
          return;
        }

        this.translate();
      });

      mouseup$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.delta = {x: 0, y: 0};
      });
    });
  }

  private calculateAvailableArea(): void {
    this.setPageSize();
    this.setClientSize();

    const topLeft: IVector2 = {
      x: this.draggableAreaXMargin,
      y: this.draggableAreaTopMargin
    };
    const bottomRight: IVector2 = {
      x: this.pageSize.x - (this.clientSize.x + this.draggableAreaXMargin),
      y: this.pageSize.y - (this.clientSize.y + this.draggableAreaBotMargin)
    };

    this.availableCoords = {topLeft, bottomRight};
  }

  private setPageSize(): void {
    this.pageSize = {
      x: window.document.body.clientWidth,
      y: window.document.body.clientHeight
    };
  }

  private setClientSize(): void {
    this.clientSize = {
      x: this.target.offsetWidth,
      y: this.target.offsetHeight
    };
  }

  private translate() {
    requestAnimationFrame(() => {
      this.target.style.left = `${this.getStyleLeft()}px`;
      this.target.style.top = `${this.getStyleTop()}px`;
    });
  }

  private getStyleLeft(): number {
    const value = this.startOffset[0] + this.delta.x;

    return this.draggableWithRestrictArea
      ? this.clamp(value, this.availableCoords.topLeft.x, this.availableCoords.bottomRight.x)
      : value;
  }

  private getStyleTop(): number {
    const value = this.startOffset[1] + this.delta.y;

    return this.draggableWithRestrictArea
      ? this.clamp(value, this.availableCoords.topLeft.y, this.availableCoords.bottomRight.y)
      : value;
  }

  private clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }
}
